import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { t } from 'i18next';
import i18n from '../i18n';
import { speechToTextSilenceTime } from '../config/constants';

dayjs.extend(utc);

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;
};

// "2023-10-09" this date to "09 Oct 2023" format
export const formatNumberToStringMonth = (inputDate: string) => {
  const months = [
    i18n.t('common.monthsShort.jan'),
    i18n.t('common.monthsShort.feb'),
    i18n.t('common.monthsShort.mar'),
    i18n.t('common.monthsShort.apr'),
    i18n.t('common.monthsShort.may'),
    i18n.t('common.monthsShort.jun'),
    i18n.t('common.monthsShort.jul'),
    i18n.t('common.monthsShort.aug'),
    i18n.t('common.monthsShort.sep'),
    i18n.t('common.monthsShort.oct'),
    i18n.t('common.monthsShort.nov'),
    i18n.t('common.monthsShort.dec'),
  ];

  const date = new Date(inputDate);
  const day = String(date.getDate()).padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

export const formatNumberToStringMonthWithoutYear = (inputDate?: string) => {
  const months = [
    i18n.t('common.monthsShort.jan'),
    i18n.t('common.monthsShort.feb'),
    i18n.t('common.monthsShort.mar'),
    i18n.t('common.monthsShort.apr'),
    i18n.t('common.monthsShort.may'),
    i18n.t('common.monthsShort.jun'),
    i18n.t('common.monthsShort.jul'),
    i18n.t('common.monthsShort.aug'),
    i18n.t('common.monthsShort.sep'),
    i18n.t('common.monthsShort.oct'),
    i18n.t('common.monthsShort.nov'),
    i18n.t('common.monthsShort.dec'),
  ];

  const date = inputDate ? new Date(inputDate) : new Date();

  const day = String(date.getDate()).padStart(2, '0');
  const month = months[date.getMonth()];

  return `${day} ${month}`;
};

export const weekdays = [
  i18n.t('common.days.sunday'),
  i18n.t('common.days.monday'),
  i18n.t('common.days.tuesday'),
  i18n.t('common.days.wednesday'),
  i18n.t('common.days.thursday'),
  i18n.t('common.days.friday'),
  i18n.t('common.days.saturday'),
];

// TODO: have to confirm week start day from mobile team
/**
 * Converts an array of binary values into a string of days of the week.
 *
 * @param binaryDays
 * - An array of seven numbers, each either 1 or 0, representing the days of the week from Monday to Sunday.
 *
 * @returns A string of the selected days of the week, separated by commas.
 * If a day is represented by 1 in the array, it is included in the string.
 *  If it is represented by 0, it is not included.
 *
 * @example
 * Returns "Monday, Wednesday, Friday, Sunday"
 * getDaysFromBinaryArray([1, 0, 1, 0, 1, 0, 1]);
 *
 * @example
 * Returns "Tuesday, Thursday, Saturday"
 * getDaysFromBinaryArray([0, 1, 0, 1, 0, 1, 0]);
 */
export const getDaysFromBinaryArray = (binaryDays: number[]): string => {
  const daysOfWeek = [
    i18n.t('common.daysShort.sun'),
    i18n.t('common.daysShort.mon'),
    i18n.t('common.daysShort.tue'),
    i18n.t('common.daysShort.wed'),
    i18n.t('common.daysShort.thu'),
    i18n.t('common.daysShort.fri'),
    i18n.t('common.daysShort.sat'),
  ];
  // Map over the array and select the days where the value is 1
  const selectedDays = binaryDays
    .map((value, index) => (value === 1 ? daysOfWeek[index] : null))
    .filter((day) => day !== null);
  return selectedDays.join(', ');
};

/**
 * Converts a date range string into two ISO string dates.
 *
 * @param dateRange - A string representing a range of dates in the format "Month Day – Month Day" or "Month Day – Day".
 * @param year - The year for the date range.
 * @return An object with `from` and `to` properties representing the start and end dates of the range, respectively.
 *
 * @example
 * @Case1
 *  Returns { from: "2022-01-01T00:00:00.000Z", to: "2022-02-02T23:59:59.999Z" }
 * convertDateRange("January 1 – February 2", 2022);
 *
 * @Case2
 *  Returns { from: "2022-01-01T00:00:00.000Z", to: "2022-01-15T23:59:59.999Z" }
 * convertDateRange("January 1 – 15", 2022);
 */

export const convertDateRange = (dateRange: string, year: number) => {
  const [startMonthDay, endMonthDayOrDay] = dateRange.split(' – ');
  // Split start date into month and day
  const [startMonth, startDay] = startMonthDay.split(' ');
  // Check if the end part contains both month and day or just day
  let endMonth;
  let endDay;
  if (endMonthDayOrDay.includes(' ')) {
    [endMonth, endDay] = endMonthDayOrDay.split(' ');
  } else {
    endMonth = startMonth;
    endDay = endMonthDayOrDay;
  }
  const startDate = dayjs(`${year}-${startMonth}-${startDay}`).startOf('day').toISOString();
  const endDate = dayjs(`${year}-${endMonth}-${endDay}`).endOf('day').toISOString();

  return { from: startDate, to: endDate };
};

export const getFullDayName = (abbreviatedDay: string): string => {
  const daysMap: { [key: string]: string } = {
    Sun: i18n.t('common.days.sunday'),
    Mon: i18n.t('common.days.monday'),
    Tue: i18n.t('common.days.tuesday'),
    Wed: i18n.t('common.days.wednesday'),
    Thu: i18n.t('common.days.thursday'),
    Fri: i18n.t('common.days.friday'),
    Sat: i18n.t('common.days.saturday'),
  };

  const fullDayName = daysMap[abbreviatedDay];
  return fullDayName || 'Invalid day';
};

/**
 * Converts a date string into a 12-hour format.
 *
 * @param dateString - A string representing a date.
 *
 * @returns A string representing the time in 12-hour format,
 *  or an empty string if the minutes part of the time is '00'. Returns null if the input is not a valid date string.
 *
 * @example
 *  Returns "2 PM"
 * convertTo12HourFormat("2022-01-01T14:00:00.000Z");
 *
 * @example
 * Returns ""
 * convertTo12HourFormat("2022-01-01T14:00:00.000Z");
 */
export const convertTo12HourFormat = (dateString: string): string | null => {
  const date = dayjs(dateString);
  return date.format('h:mm A').split(' ')[0].split(':')[1] === '00' ? date.format('h A') : '';
};

export function getToday() {
  return dayjs().startOf('day').toISOString();
}

export function getEndOfToday() {
  return dayjs().endOf('day').toISOString();
}

export function getYesterday() {
  return dayjs().subtract(1, 'day').startOf('day').toISOString();
}

export function getEndOfYesterday() {
  return dayjs().subtract(1, 'day').endOf('day').toISOString();
}

export function getLast7DaysStart() {
  return dayjs().subtract(7, 'days').startOf('day').toISOString();
}

export function getLast7DaysEnd() {
  return dayjs().subtract(2, 'days').endOf('day').toISOString();
}

export function getLast7Days() {
  return dayjs().subtract(7, 'days').startOf('day').toISOString();
}

export function getNow() {
  return dayjs().toISOString();
}

// this function only supports for english language
export const getStartAndEndOfMonth = (monthYearString: string) => {
  const [month, year] = monthYearString.split(' ');
  const yearNumber = parseInt(year, 10);
  const startDate = new Date(
    yearNumber,
    new Date(Date.parse(`${month} 1, ${yearNumber}`)).getMonth(),
    1
  );
  const endDate = new Date(
    yearNumber,
    new Date(Date.parse(`${month} 1, ${yearNumber}`)).getMonth() + 1,
    0
  );
  const startISO = startDate.toISOString();
  const endISO = endDate.toISOString();

  return { startISO, endISO };
};

export const isOlderThanSpecifiedSeconds = (timestamp: number) => {
  const currentTime = Math.floor(Date.now() / 1000);
  const timeDifference = currentTime - timestamp;
  return timeDifference > Number(speechToTextSilenceTime);
};

// engagement server don't return data for all days, so we need to fill the missing days with NO_DATA
export const transformEngagementData = (
  data: Array<{ date: string; engagementLevel: 'NO_DATA' | 'NONE' | 'PARTIAL' | 'FULL' }>
) => {
  const result: Array<{ date: string; engagementLevel: 'NO_DATA' | 'NONE' | 'PARTIAL' | 'FULL' }> =
    [];
  const today = new Date();

  for (let i = 0; i < 14; i += 1) {
    const date = new Date();
    date.setDate(today.getDate() - i);
    const dateString = date.toISOString().split('T')[0];

    const existingData = data.find((d) => d.date.startsWith(dateString));
    if (existingData) {
      result.push(existingData);
    } else {
      result.push({ date: `${dateString}T00:00:00.000Z`, engagementLevel: 'NO_DATA' });
    }
  }
  return result;
};

export const transformOverallEngagementData = (
  data: Array<{ date: string; engagementLevel: 'NO_DATA' | 'NONE' | 'PARTIAL' | 'FULL' }>
) => {
  data.sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);

    if (dateA < dateB) {
      return -1;
    }
    if (dateA > dateB) {
      return 1;
    }
    return 0;
  });
  const result: Array<{ date: string; engagementLevel: 'NO_DATA' | 'NONE' | 'PARTIAL' | 'FULL' }> =
    [];
  const startDate = new Date(data[0]?.date);
  const endDate = new Date(data[data.length - 1]?.date);

  while (startDate <= endDate) {
    const dateString = startDate.toISOString().split('T')[0];
    const existingData = data.find((d) => d.date.startsWith(dateString));
    if (existingData) {
      result.push(existingData);
    } else {
      result.push({ date: `${dateString}T00:00:00.000Z`, engagementLevel: 'NO_DATA' });
    }
    startDate.setDate(startDate.getDate() + 1);
  }
  return result;
};

export const formatDateToLocal = (utcDate: string): string =>
  dayjs(utcDate).local().format('DD MMM YYYY');

export const appendCurrentTimeToDate = (expDate: Date | string) => {
  let date: Date;

  if (typeof expDate === 'string') {
    date = new Date(expDate);
  } else {
    date = expDate;
  }

  const currentTime = new Date();
  currentTime.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
  return currentTime.toISOString();
};

export const formattedDate = (date: string): string => {
  const originalDate = new Date(date);
  const day = originalDate.getDate();
  const month = originalDate.toLocaleString('default', { month: 'short' });
  const hours = originalDate.getHours();
  const minutes = originalDate.getMinutes();

  // Formatting the date
  return `${day} ${t(`common.monthsShort.${month.toLocaleLowerCase()}`)} (${hours}:${
    minutes < 10 ? '0' : ''
  }${minutes})`;
};

export function formatDateTime(inputDate: string): {
  formattedDay: string;
  formattedTime: string;
} {
  const date = new Date(inputDate);
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;

  const formattedDay = `${day}/${month < 10 ? '0' : ''}${month}/${year}`;
  const formattedTime = `${formattedHours}:${minutes < 10 ? '0' : ''}${minutes} ${ampm}`;

  return { formattedDay, formattedTime };
}

export const formatDateForChat = (dateString: string) => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return i18n.t('notifications.today');
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return i18n.t('notifications.yesterday');
  }
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const extractTimeFromDate = (dateString: string): string => {
  const date = new Date(dateString);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

export function getTimeDifference(inputDate: string): string {
  const inputDateTime = new Date(inputDate);
  const currentTime = new Date();

  const timeDifference = Math.abs(currentTime.getTime() - inputDateTime.getTime());
  const secondsDifference = Math.floor(timeDifference / 1000);
  const minutesDifference = Math.floor(secondsDifference / 60);
  const hoursDifference = Math.floor(minutesDifference / 60);
  const daysDifference = Math.floor(hoursDifference / 24);

  if (daysDifference > 0) {
    return `${daysDifference} ${
      daysDifference === 1 ? i18n.t('chat.day') : i18n.t('chat.days')
    } ${i18n.t('chat.ago')}`;
  }
  if (hoursDifference > 0) {
    return `${hoursDifference} ${
      hoursDifference === 1 ? i18n.t('chat.hour') : i18n.t('chat.hours')
    } ${i18n.t('chat.ago')}`;
  }
  if (minutesDifference > 0) {
    return `${minutesDifference} ${
      minutesDifference === 1 ? i18n.t('chat.minute') : i18n.t('chat.minutes')
    } ${i18n.t('chat.ago')}`;
  }
  if (secondsDifference > 0) {
    return `${secondsDifference} ${
      secondsDifference === 1 ? i18n.t('chat.second') : i18n.t('chat.seconds')
    } ${i18n.t('chat.ago')}`;
  }
  return i18n.t('chat.justNow');
}

export const formatTime = (time: number) => {
  if (Number.isNaN(time)) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.round(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export const formatTimeToText = (time: number) => {
  const hours = Math.floor(time / 60);
  const mins = Math.round(time % 60);
  const hoursText = hours > 0 ? `${hours}h ` : '';
  const minsText = mins > 0 ? `${mins}min` : '';
  return hoursText + minsText;
};
